import type {
  Invitation,
  InvitationWithWorkspace,
  WorkspaceInvitation,
  InvitationStatus,
  CreateInvitationDto,
  UpdateInvitationDto,
} from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const InvitationStatusSchema = z.enum([
  "pending",
  "accepted",
  "rejected",
  "cancelled",
]) as z.ZodType<InvitationStatus>;

const InvitationBaseSchema = z.object({
  id: z.number(),
  workspaceId: z.number(),
  inviterId: z.number(),
  inviteeEmail: z.string(),
  inviteeId: z.number().nullish(),
  status: InvitationStatusSchema,
  emailSent: z.boolean(),
  emailSentAt: z.coerce.date().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const InvitationSchema: z.ZodType<Invitation> = InvitationBaseSchema;

const InvitationWithWorkspaceSchema: z.ZodType<InvitationWithWorkspace> =
  InvitationBaseSchema.extend({
    workspace: z.object({
      id: z.number(),
      name: z.string(),
      thumbnailUrl: z.string().nullish(),
    }),
    inviter: z.object({
      id: z.number(),
      email: z.string(),
      nickname: z.string().nullish(),
      avatarUrl: z.string().nullish(),
    }),
  });

const WorkspaceInvitationSchema: z.ZodType<WorkspaceInvitation> = z.object({
  id: z.number(),
  workspaceId: z.number(),
  inviterId: z.number(),
  inviteeEmail: z.string(),
  inviteeId: z.number().nullish(),
  status: InvitationStatusSchema,
  createdAt: z.coerce.date(),
  inviter: z.object({
    id: z.number(),
    email: z.string(),
    nickname: z.string().nullish(),
    avatarUrl: z.string().nullish(),
  }),
  invitee: z
    .object({
      id: z.number(),
      email: z.string(),
      nickname: z.string().nullish(),
      avatarUrl: z.string().nullish(),
    })
    .nullish(),
});

// Response Schemas
const CreateInvitationResponseSchema: z.ZodType<
  DataAndMessageResponse<Invitation>
> = z.object({
  data: InvitationSchema,
  message: z.string(),
});

const GetInvitationsResponseSchema: z.ZodType<
  DataAndMessageResponse<InvitationWithWorkspace[]>
> = z.object({
  data: z.array(InvitationWithWorkspaceSchema),
  message: z.string(),
});

const UpdateInvitationResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const GetWorkspaceInvitationsResponseSchema: z.ZodType<
  DataAndMessageResponse<WorkspaceInvitation[]>
> = z.object({
  data: z.array(WorkspaceInvitationSchema),
  message: z.string(),
});

const CancelInvitationResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

export function createInvitationModule(httpClient: HttpClient) {
  return {
    create: async (workspaceId: number, data: CreateInvitationDto) => {
      const response = await httpClient.post<
        typeof CreateInvitationResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/invitations`, {
        data,
        schema: CreateInvitationResponseSchema,
      });
      return response.data;
    },

    listMyInvitations: async () => {
      const response = await httpClient.get<
        typeof GetInvitationsResponseSchema
      >("/api/v1/invitations", {
        schema: GetInvitationsResponseSchema,
      });
      return response.data;
    },

    accept: async (invitationId: number) => {
      const data: UpdateInvitationDto = { action: "accepted" };
      const response = await httpClient.patch<
        typeof UpdateInvitationResponseSchema
      >(`/api/v1/invitations/${invitationId}`, {
        data,
        schema: UpdateInvitationResponseSchema,
      });
      return response;
    },

    reject: async (invitationId: number) => {
      const data: UpdateInvitationDto = { action: "rejected" };
      const response = await httpClient.patch<
        typeof UpdateInvitationResponseSchema
      >(`/api/v1/invitations/${invitationId}`, {
        data,
        schema: UpdateInvitationResponseSchema,
      });
      return response;
    },

    listWorkspaceInvitations: async (workspaceId: number) => {
      const response = await httpClient.get<
        typeof GetWorkspaceInvitationsResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/invitations`, {
        schema: GetWorkspaceInvitationsResponseSchema,
      });
      return response.data;
    },

    cancel: async (workspaceId: number, invitationId: number) => {
      const response = await httpClient.delete<
        typeof CancelInvitationResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/invitations/${invitationId}`, {
        schema: CancelInvitationResponseSchema,
      });
      return response;
    },
  };
}
